'use client';

import {
	Dialog,
	DialogBackdrop,
	DialogPanel,
	DialogTitle,
} from '@headlessui/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMetrica } from 'next-yandex-metrica';
import { feedbackAIProblemAction } from '@/features/feedback/actions/feedback-ai-problem-action';
import {
	AIFeedbackAnalytics,
	AIFeedbackSubmission,
	AI_FEEDBACK_COMMENT_LIMIT,
	buildAIFeedback,
	type AIFeedbackError,
	type AIFeedbackContext,
	type AIFeedbackFlow,
} from '@/features/feedback/lib/ai-feedback';

import {
	FEEDBACK_ISSUE_OPTIONS,
	type FeedbackSentiment,
} from '../lib/feedback-options';
import { Button } from '@/shared';

type FeedbackDialogProps = {
	context: AIFeedbackContext;
	sentiment: FeedbackSentiment;
	problemId: string;
	problemTitle: string;
	flow: AIFeedbackFlow;
	onClose: () => void;
	onSaved: () => void;
};

export function FeedbackDialog({
	context,
	sentiment,
	onClose,
	onSaved,
	problemId,
	problemTitle,
	flow,
}: FeedbackDialogProps) {
	const [issueType, setIssueType] = useState('');
	const [details, setDetails] = useState('');

	const [pending, setPending] = useState(false);
	const [error, setError] = useState<AIFeedbackError | null>(null);
	const { reachGoal } = useMetrica();
	const sendGoal = useRef(reachGoal);
	sendGoal.current = reachGoal;
	const [analytics] = useState(
		() =>
			new AIFeedbackAnalytics(
				(goal, params) => sendGoal.current(goal, params),
				flow,
				sentiment,
			),
	);
	const [submission] = useState(() => new AIFeedbackSubmission(analytics));
	useLayoutEffect(() => {
		submission.setActive(true);
		return () => submission.setActive(false);
	}, [submission]);
	useEffect(() => {
		analytics.open();
	}, [analytics]);
	const errors: Record<AIFeedbackError, string> = {
		invalid_input:
			'Проверьте отзыв и выбранное сообщение, затем попробуйте снова.',
		unauthorized: 'Сессия истекла. Войдите в аккаунт и повторите отправку.',
		forbidden: 'Нет доступа к этой AI-задаче.',
		not_found: 'AI-задача или выбранное сообщение больше недоступны.',
		save_failed: 'Не удалось сохранить отзыв. Попробуйте ещё раз.',
	};

	const isNegative = sentiment === 'down';

	const reset = () => {
		setIssueType('');
		setDetails('');
	};

	const handleClose = (source: 'cancel' | 'dismiss') => {
		if (!submission.canClose()) return;
		analytics.track('CLOSE', { source, has_text: !!details.trim() });
		reset();
		onClose();
	};

	const handleSubmit = async () => {
		analytics.textCompleted(!!details.trim());
		await submission.submit(
			buildAIFeedback(
				problemId,
				problemTitle,
				{
					sentiment,
					issueType,
					details,
				},
				context,
			),
			feedbackAIProblemAction,
			{
				pending: setPending,
				error: setError,
				success: () => {
					reset();
					onSaved();
				},
			},
		);
	};

	return (
		<Dialog
			open
			onClose={() => handleClose('dismiss')}
			className="relative z-50"
		>
			<DialogBackdrop className="fixed inset-0 bg-gray-1/40 backdrop-blur-[2px]" />
			<div className="fixed inset-0 flex w-screen items-center justify-center p-4">
				<DialogPanel className="relative w-full max-w-[560px] rounded-2xl border border-gray-4 bg-white p-8 shadow-md">
					<DialogTitle className="text-2xl font-semibold text-gray-1">
						{isNegative ? 'Что не так с ответом?' : 'Что понравилось?'}
					</DialogTitle>

					<div className="mt-6 space-y-5">
						{isNegative && (
							<div>
								<label
									htmlFor="ai-feedback-issue"
									className="mb-2 block text-base text-gray-2"
								>
									Тип проблемы:{' '}
									<span className="text-gray-3">(необязательно)</span>
								</label>
								<select
									id="ai-feedback-issue"
									value={issueType}
									disabled={pending}
									onChange={(e) => {
										setIssueType(e.target.value);
										analytics.track('CHOICE', { option_id: e.target.value });
									}}
									className="h-11 w-full cursor-pointer appearance-none rounded-lg border border-gray-4 bg-white pl-3 pr-9 text-base text-black focus:border-gray-3 focus:outline-none"
									style={{
										backgroundImage:
											"url(\"data:image/svg+xml;charset=utf-8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
										backgroundRepeat: 'no-repeat',
										backgroundPosition: 'right 12px center',
									}}
								>
									<option value="">Выберите…</option>
									{FEEDBACK_ISSUE_OPTIONS.map((opt) => (
										<option key={opt.value} value={opt.value}>
											{opt.label}
										</option>
									))}
								</select>
							</div>
						)}

						<div>
							<label
								htmlFor="ai-feedback-details"
								className="mb-2 block text-base text-gray-2"
							>
								Подробности:{' '}
								<span className="text-gray-3">(необязательно)</span>
							</label>
							<textarea
								id="ai-feedback-details"
								value={details}
								disabled={pending}
								onChange={(e) => {
									setDetails(
										Array.from(e.target.value)
											.slice(0, AI_FEEDBACK_COMMENT_LIMIT)
											.join(''),
									);
									if (e.target.value) analytics.textStarted();
								}}
								onBlur={() => analytics.textCompleted(!!details.trim())}
								aria-describedby="ai-feedback-details-hint"
								placeholder={
									isNegative
										? 'Что не так с этим ответом?'
										: 'Что было хорошего в ответе?'
								}
								rows={4}
								autoFocus
								className="w-full resize-none rounded-lg border border-gray-4 bg-white px-3 py-2.5 text-base text-black placeholder:text-gray-3 focus:border-gray-3 focus:outline-none"
							/>
							<p
								id="ai-feedback-details-hint"
								className="text-sm italic text-gray-3"
							>
								Отзыв будет связан с выбранным сообщением и этим диалогом, даже
								если вы начнёте новый диалог.
								<span className="block">
									{Array.from(details).length} / {AI_FEEDBACK_COMMENT_LIMIT}
								</span>
							</p>
						</div>
					</div>

					<div aria-live="polite">
						{error && (
							<p role="alert" className="mt-4 text-sm text-red-600">
								{errors[error]}
							</p>
						)}
					</div>
					<div className="mt-6 flex justify-end gap-3">
						<Button
							type="button"
							onClick={() => handleClose('cancel')}
							disabled={pending}
							variant="outline"
						>
							Отмена
						</Button>
						<Button
							type="button"
							onClick={handleSubmit}
							disabled={pending}
							variant="primary"
						>
							{pending
								? 'Сохраняем…'
								: error
									? 'Повторить отправку'
									: 'Отправить'}
						</Button>
					</div>
				</DialogPanel>
			</div>
		</Dialog>
	);
}
